/**
 * SCHOOL TAXONOMY SERVICE
 * 
 * Gestiona las taxonomías configurables por escuela
 */

import { db } from '@/core/config/firebase';
import { Collections } from '@/core/config/collections';
import { Logger } from '@/core/utils';
import type { 
  SchoolTaxonomy, 
  CreateSchoolTaxonomyDTO, 
  UpdateSchoolTaxonomyDTO,
  PREDEFINED_TAXONOMIES
} from '@/core/types/models/content/school-taxonomy.model';
import { PREDEFINED_TAXONOMIES as TAXONOMIES } from '@/core/types/models/content/school-taxonomy.model';

const logger = Logger.getLogger('SchoolTaxonomyService');

class SchoolTaxonomyService {
  private collection = db.collection(Collections.SCHOOL_TAXONOMIES);

  /**
   * Crear taxonomía para una escuela
   */
  async create(dto: CreateSchoolTaxonomyDTO): Promise<SchoolTaxonomy> {
    try {
      logger.info('Creating school taxonomy', { schoolId: dto.schoolId, name: dto.name });

      // Si ya existe una taxonomía activa, desactivarla
      const existing = await this.getActive(dto.schoolId);
      if (existing) {
        await this.update(existing.id, { isActive: false });
      }

      const now = new Date();
      const taxonomyData: Omit<SchoolTaxonomy, 'id'> = {
        ...dto,
        categories: dto.categories.map((cat: any, index: number) => ({
          ...cat,
          id: cat.id || `cat_${Date.now()}_${index}`,
          order: cat.order ?? index
        })),
        isActive: true,
        createdAt: now,
        updatedAt: now
      };

      const docRef = await this.collection.add(taxonomyData);
      logger.info('School taxonomy created', { id: docRef.id });

      return { id: docRef.id, ...taxonomyData };
    } catch (error) {
      logger.error('Failed to create taxonomy', error);
      throw error;
    }
  }

  /**
   * Obtener taxonomía activa de una escuela
   */
  async getActive(schoolId: string): Promise<SchoolTaxonomy | null> {
    try {
      const snapshot = await this.collection
        .where('schoolId', '==', schoolId)
        .where('isActive', '==', true)
        .limit(1)
        .get();

      if (snapshot.empty) {
        logger.info('No active taxonomy found', { schoolId });
        return null;
      }

      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() } as SchoolTaxonomy;
    } catch (error) {
      logger.error('Failed to get active taxonomy', error);
      throw error;
    }
  }

  /**
   * Obtener todas las taxonomías de una escuela
   */
  async getBySchool(schoolId: string): Promise<SchoolTaxonomy[]> {
    try {
      const snapshot = await this.collection
        .where('schoolId', '==', schoolId)
        .orderBy('createdAt', 'desc')
        .get();

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as SchoolTaxonomy[];
    } catch (error) {
      logger.error('Failed to get taxonomies', error);
      throw error;
    }
  }

  /**
   * Actualizar taxonomía
   */
  async update(id: string, dto: UpdateSchoolTaxonomyDTO): Promise<void> {
    try {
      logger.info('Updating taxonomy', { id });

      await this.collection.doc(id).update({
        ...dto,
        updatedAt: new Date()
      });

      logger.info('Taxonomy updated', { id });
    } catch (error) {
      logger.error('Failed to update taxonomy', error);
      throw error;
    }
  }

  /**
   * Activar una taxonomía (y desactivar las demás)
   */
  async setActive(id: string): Promise<void> {
    try {
      logger.info('Setting taxonomy as active', { id });

      // Obtener la taxonomía para saber el schoolId
      const doc = await this.collection.doc(id).get();
      if (!doc.exists) {
        throw new Error('Taxonomy not found');
      }

      const taxonomy = doc.data() as SchoolTaxonomy;

      // Desactivar todas las del mismo school
      const snapshot = await this.collection
        .where('schoolId', '==', taxonomy.schoolId)
        .where('isActive', '==', true)
        .get();

      const batch = db.batch();
      snapshot.docs.forEach(d => {
        batch.update(d.ref, { isActive: false, updatedAt: new Date() });
      });

      // Activar la seleccionada
      batch.update(this.collection.doc(id), { 
        isActive: true, 
        updatedAt: new Date() 
      });

      await batch.commit();
      logger.info('Taxonomy activated', { id });
    } catch (error) {
      logger.error('Failed to set active taxonomy', error);
      throw error;
    }
  }

  /**
   * Eliminar taxonomía
   */
  async delete(id: string): Promise<void> {
    try {
      logger.info('Deleting taxonomy', { id });
      await this.collection.doc(id).delete();
      logger.info('Taxonomy deleted', { id });
    } catch (error) {
      logger.error('Failed to delete taxonomy', error);
      throw error;
    }
  }

  /**
   * Crear taxonomía desde una predefinida
   */
  async createFromPredefined(
    schoolId: string, 
    predefinedKey: keyof typeof PREDEFINED_TAXONOMIES
  ): Promise<SchoolTaxonomy> {
    try {
      const predefined = TAXONOMIES[predefinedKey];
      if (!predefined) {
        throw new Error(`Predefined taxonomy '${predefinedKey}' not found`);
      }

      logger.info('Creating taxonomy from predefined', { 
        schoolId, 
        predefinedKey 
      });

      return await this.create({
        schoolId,
        name: predefined.name,
        description: predefined.description,
        categories: predefined.categories.map(cat => ({
          id: cat.id,
          name: cat.name,
          description: cat.description,
          color: cat.color,
          icon: cat.icon,
          keywords: cat.keywords,
          order: cat.order
        }))
      });
    } catch (error) {
      logger.error('Failed to create from predefined', error);
      throw error;
    }
  }

  /**
   * Obtener taxonomías predefinidas disponibles
   */
  getPredefinedTaxonomies() {
    return TAXONOMIES;
  }
}

export const schoolTaxonomyService = new SchoolTaxonomyService();



